import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {firstValueFrom, Subject} from 'rxjs';
import {NgbModal, NgbModalConfig} from "@ng-bootstrap/ng-bootstrap";
import {
	AuthService,
	ConfirmDialogService,
	EchoService,
	NotificationService,
	SettingsService,
	SnackBarService
} from "@customcoding/shared";
import {TauriService} from "./scap/shared/services/tauri.service";
import {getVersion} from "@tauri-apps/api/app";
import {TauriFilesystemService} from "./scap/shared/services/tauri-filesystem.service";
import {DoctorService} from "./scap/shared/services/doctor.service";
import {EntityPreloadService} from "./scap/shared/services/entity-preload.service";
import {take, tap} from "rxjs/operators";
import {PatientService} from "./scap/shared/services/patient.service";
import {AppointmentService} from "./scap/shared/services/appointment.service";

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	providers: [NgbModalConfig, NgbModal]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

	lang = new Subject<string>();
	title = 'dashboard-app';
	demo = false;

	constructor(
		private pushService: EchoService,
		private tauri: TauriService,
		private tauriFileService: TauriFilesystemService,
		private doctorService: DoctorService,
		private settings: SettingsService,
		private ngbModalConfig: NgbModalConfig,
		private authService: AuthService,
		private entityPreloadService: EntityPreloadService,
		private patientService: PatientService,
		private notificationService: NotificationService,
		private confirmDialog: ConfirmDialogService,
		private appointmentService: AppointmentService,
		private snackbar: SnackBarService
	) {
		this.setUpNGBConfigs();
	}


	ngOnInit(): void {

		if (this.tauri.isDesktopApp()) {
			this.setAppVersionMeta();
			this.tauri.initDesktopFeatures();
			this.entityPreloadService.entityPreloadingComplete.pipe(
				take(1),
				tap(async () => {
					MoConsole.log('entity preloading complete ======>');
					if (await this.doctorService.currentUserIsDoctor()) {
						// await this.tauriFileService.offlineSyncLabFolder();
						this.tauriFileService.initialFoldersFilesSync().then();
						this.tauriFileService.watchLabFolderChanges().then();
					}
				})
			).subscribe();
		} else {

			this.authService.getCurrentUserAsync().then((user) => {
				this.patientService.saveAllPatientsOnInit().subscribe();
			});

		}

		this.demo = this.settings.get('BETA');

		this.notificationService.customTabs.set([{
			title: 'Terminanfragen',
			notificationNamesToInclude: [
				'ADDED_TO_APPOINTMENT_AWAITING_CONFIRM'
			]
		}])
		this.notificationService.buttonMappings.set([
			{
				key: 'SCAP_ATTENDEE_DECLINE',
				iconClass: 'la-thumbs-down',
				cssClass: 'btn-danger',
				separateFromDefault: true,
				tooltip: 'Termin ablehnen',
				callback: (notification) => {
					if (!notification?.data?.url?.id) {
						this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden');
						return;
					}
					this.confirmDialog.yesNo(
						'Sind Sie sicher dass Sie diese Terminanfrage ablehnen möchten?',
						'Sie können diese Aktion nicht rückgängig machen.'
					).subscribe(result => {
						if (result) {
							firstValueFrom(
								this.appointmentService.declineAppointmentRequest(notification.data.url.id)
							).then(() => {
								this.snackbar.showSuccessDialog('Der Termin wurde erfolgreich abgelehnt');
								if (!notification.read_at) {
									this.notificationService.markAsRead(notification);
								}
							}).catch(error => {
								this.snackbar.showServerError('Der Termin konnte nicht abgelehnt werden', error);
							});
						}
					})
				}
			},
			{
				key: 'SCAP_ATTENDEE_ACCEPT',
				iconClass: 'la-thumbs-up',
				cssClass: 'btn-success',
				separateFromDefault: true,
				tooltip: 'Termin akzeptieren',
				callback: (notification) => {
					if (!notification?.data?.url?.id) {
						this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden');
						return;
					}
					this.confirmDialog.yesNo(
						'Sind Sie sicher dass Sie diese Terminanfrage annehmen möchten?',
						'Sie können diese Aktion nicht rückgängig machen.'
					).subscribe(result => {
						if (result) {
							firstValueFrom(
								this.appointmentService.acceptAppointmentRequest(notification.data.url.id)
							).then(() => {
								this.snackbar.showSuccessDialog('Der Termin wurde erfolgreich angenommen');
								if (!notification.read_at) {
									this.notificationService.markAsRead(notification);
								}
							}).catch(error => {
								this.snackbar.showServerError('Der Termin konnte nicht angenommen werden', error);
							});
						}
					})
				}
			},
			{
				key: 'SCAP_APPOINTMENT_USE_PLANNED_STATE',
				iconClass: 'la-check',
				cssClass: 'btn-success',
				separateFromDefault: true,
				tooltip: 'Termin festlegen',
				callback: (notification) => {
					if (!notification?.data?.url?.id) {
						this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden');
						return;
					}
					firstValueFrom(this.appointmentService.get(notification.data.url.id)).then((appointment) => {
						if (!appointment) {
							this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden');
							return;
						}
						if (!appointment.plannedAppointmentStatus_id) {
							this.snackbar.showErrorDialog('Der Termin ist schon festgelegt');
							return;
						}
						this.confirmDialog.yesNo(
							'Sind Sie sicher dass Sie diesen Termin festlegen wollen?',
							'Bitte vergewissern Sie sich dass die gewünschten Mitarbeiter teilnehmen können. Sie können diese Aktion nicht rückgängig machen.'
						).subscribe(result => {
							if (result) {
								firstValueFrom(
									this.appointmentService.confirmPlannedAppointment(notification.data.url.id, appointment.plannedAppointmentStatus_id)
								).then(() => {
									this.snackbar.showSuccessDialog('Der Termin wurde erfolgreich festgelegt');
									if (!notification.read_at) {
										this.notificationService.markAsRead(notification);
									}
									this.notificationService.markAsRead(notification);
								}).catch(error => {
									this.snackbar.showServerError('Der Termin konnte nicht festgelegt werden', error);
								})
							}
						})
					}).catch(err => {
						this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden.')
					})
				}
			},
			{
				key: 'SCAP_APPOINTMENT_DELETE',
				iconClass: 'la-trash',
				cssClass: 'btn-danger',
				separateFromDefault: true,
				tooltip: 'Termin löschen',
				callback: (notification) => {
					if (!notification?.data?.url?.id) {
						this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden');
						return;
					}
					firstValueFrom(this.appointmentService.get(notification.data.url.id)).then((appointment) => {
						if (!appointment) {
							this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden');
							return;
						}
						this.confirmDialog.yesNo(
							'Sind Sie sicher dass Sie diesen Termin löschen wollen?',
							'Sie können diese Aktion nicht rückgängig machen.'
						).subscribe(result => {
							if (result) {
								firstValueFrom(
									this.appointmentService.deleteAppointment(notification.data.url.id)
								).then(() => {
									this.snackbar.showSuccessDialog('Der Termin wurde erfolgreich gelöscht');
									if (!notification.read_at) {
										this.notificationService.markAsRead(notification);
									}
								}).catch(error => {
									this.snackbar.showServerError('Der Termin konnte nicht gelöscht werden', error);
								})
							}
						})
					}).catch(err => {
						this.snackbar.showErrorDialog('Der Termin konnte nicht gefunden werden.')
					})
				}
			}
		])
	}

	ngAfterViewInit() {
		MoConsole.log('current user', this.authService.getCurrentUser());
	}

	async setAppVersionMeta() {
		const appVersion = await getVersion();
		this.settings.set('VERSION', appVersion + '-desktop')
	}

	ngOnDestroy() {
		this.pushService.leavePushChannel();
	}

	private setUpNGBConfigs() {
		this.ngbModalConfig.scrollable = true;
	}

}
